import * as React from 'react';
import { graphql } from 'gatsby';
import { rem } from 'polished';
import { css } from '@emotion/css';
import formatDate from 'date-fns/format';

import { LinkType, parseLink } from '../link';
import { styled } from './themeContext';
import SocialLinks from './footer/SocialLinks';
import Link from './Link';
import { ReactComponent as _DevsistersCi } from './footer/devsisters.svg';

type Entry = {
  link: LinkType,
  label: string,
};

type SocialEntry = {
  service: NonNullable<GatsbyTypes.Footer_socialLinksFragment['snsLinks'][number]>['service'],
  entry: Entry,
};

type FooterProps = {
  companyInfo: {
    company: {
      tel: { label: string, value: string },
      fax: { label: string, value: string },
      address: { value: string },
    },
    cs: Entry,
    terms: Entry,
    privacy: Entry,
    law?: Entry,
  },
  socials: SocialEntry[],
  currentLocale: string,
  ageNoticeImageUrl: string,
};

const Container = styled.footer((props) => ({
  background: 'rgb(25, 28, 36)',
  paddingTop: rem(44),
  paddingBottom: rem(70),
  fontSize: rem(48),
  [props.theme.media['md']]: {
    fontSize: rem(70),
  },
}));

const ContentBox = styled.div((props) => ({
  display: 'flex',
  maxWidth: rem(640),
  flexDirection: 'column',
  alignItems: 'center',
  padding: `0 ${rem(30)}`,
  margin: '0 auto',
  [props.theme.media['md']]: {
    maxWidth: rem(1200),
  },
}));

const DevsistersCi = styled(_DevsistersCi)((props) => ({
  color: '#fff',
  fill: '#fff',
  fontSize: rem(29),
  [props.theme.media['md']]: {
    fontSize: rem(44),
  },
}));

const ButtonWrapper = styled.div((props) => ({
  display: 'flex',
  width: '100%',
  flexWrap: 'wrap',
  justifyContent: 'center',
  flexDirection: 'column',
  marginBottom: rem(13),
  [props.theme.media['md']]: {
    rowGap: rem(20),
    width: 'auto',
    flexDirection: 'row',
  },
}));

const ButtonLink = styled(Link)<{ bold?: boolean }>((props) => ({
  'display': 'inline-flex',
  'alignItems': 'center',
  'justifyContent': 'center',
  'textAlign': 'center',
  'whiteSpace': 'nowrap',
  'color': '#fff',
  'background': 'rgb(25, 28, 36)',
  'textDecoration': 'none',
  'border': '1px solid #fff',
  'fontSize': rem(16),
  'margin': `${rem(9)} ${rem(10)}`,
  'marginBottom': rem(0),
  'padding': `${rem(8)} ${rem(30)} ${rem(8)} ${rem(30)}`,
  'fontWeight': props.bold ? 'bold' : 'normal',
  '&:hover': {
    color: 'rgb(25, 28, 36)',
    background: '#fff',
  },
  [props.theme.media['md']]: {
    margin: `${rem(0)} ${rem(10)}`,
    padding: `${rem(11)} ${rem(30)} ${rem(11)} ${rem(30)}`,
    lineHeight: 1,
  },
}));

const InfoWrapper = styled.div({
  display: 'flex',
  justifyContent: 'center',
  flexWrap: 'wrap',
});

const InfoText = styled.span({
  fontSize: rem(16),
  margin: `${rem(3)} ${rem(5)}`,
  textAlign: 'center',
  color: '#fff',
});

const AgeNoticeWrapper = styled.div(props => ({
  flexDirection: 'column',
  alignItems: 'center',
  display: 'flex',
  marginTop: rem(60),
  [props.theme.media['md']]: {
    flexDirection: 'row',
    alignItems: 'initial',
  },
}));


const AgeNotiveImg = styled.img(props => ({
  marginBottom: rem(25),
  width: rem(92),
  height: rem(94),
  [props.theme.media['md']]: {
    marginBottom: 0,
  },
}));

const AgeNoticeList = styled.ul({
  fontSize: rem(14),
  listStyle: 'none',
  padding: 0,
  margin: 0,
  marginLeft: rem(12),
});

const AgeNotiveListItem = styled.li({
  color: 'white',
  marginBottom: rem(5),
});

const Footer: React.FC<FooterProps> = ({
  companyInfo,
  socials,
  currentLocale,
  ageNoticeImageUrl,
}) => {
  return (
    <Container>
      <ContentBox>
        {socials && (
          <div className={css({
            marginBottom: 32,
          })}>
            <SocialLinks socials={socials} />
          </div>
        )}
        <ButtonWrapper>
          <ButtonLink link={companyInfo.terms.link}>
            {companyInfo.terms.label}
          </ButtonLink>
          <ButtonLink link={companyInfo.privacy.link} bold={currentLocale === 'ko'}>
            {companyInfo.privacy.label}
          </ButtonLink>
          <ButtonLink link={companyInfo.cs.link}>
            {companyInfo.cs.label}
          </ButtonLink>
          {currentLocale === 'ja' && companyInfo.law && (
            <ButtonLink link={companyInfo.law.link}>
              {companyInfo.law.label}
            </ButtonLink>
          )}
        </ButtonWrapper>
        <InfoWrapper>
          <InfoText>{companyInfo.company.address.value}</InfoText>
        </InfoWrapper>
        <InfoWrapper>
          <InfoText>
            {`${companyInfo.company.tel.label}: ${companyInfo.company.tel.value}`}
          </InfoText>
          <InfoText>
            {`${companyInfo.company.fax.label}: ${companyInfo.company.fax.value}`}
          </InfoText>
        </InfoWrapper>
        <InfoWrapper>
          <InfoText>CookieRun® © {formatDate(Date.now(), 'yyyy')} Devsisters Corp. All Rights Reserved.</InfoText>
        </InfoWrapper>

        <div className={css({
          display: 'inline-flex',
          marginTop: 23,
          lineHeight: 1,
        })}>
          <DevsistersCi />
        </div>
        {
          currentLocale === 'zh-Hant' &&
          <AgeNoticeWrapper>
            <AgeNotiveImg src={ageNoticeImageUrl} />
            <AgeNoticeList>
              <AgeNotiveListItem>※《薑餅人王國》依遊戲軟體分級管理辦法分類為輔12級。</AgeNotiveListItem>
              <AgeNotiveListItem>※本遊戲情節涉及打鬥、攻擊畫面。</AgeNotiveListItem>
              <AgeNotiveListItem>※本遊戲為免費使用，遊戲內另提供購買虛擬遊戲幣、禮包等付費服務。</AgeNotiveListItem>
              <AgeNotiveListItem>※請注意遊戲時間，避免沉迷。</AgeNotiveListItem>
            </AgeNoticeList>
          </AgeNoticeWrapper>
        }
      </ContentBox>
    </Container>
  );
};

export default Footer;


export const fragments = graphql`
  fragment Footer_socialLinks on SiteInformation {
    snsLinks {
      label
      service
      url
    }
  }
  fragment Footer_companyInfo on SiteInformation {
    company {
      tel
      fax
      address
    }
    termsLink
    csLink
    privacyLink
  }
`;
